import React, { useEffect, useMemo, useRef } from "react";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { HvTypography, HvSwitch } from "@hitachivantara/uikit-react-core";
import {
  useTimeSettings,
  WorkflowActionData,
  WorkflowCondition,
} from "lib/api";
import { locationAttribute } from "../QueryBuilder/queryToWorkflowConditions";
import { workflowConditionsToQuery } from "../QueryBuilder/workflowConditionsToQuery";
import { queryToMongo } from "../QueryBuilder/queryToMongo";

export const HasConditionsValid = (
  conditions: WorkflowCondition[],
  timezone: string
): boolean => {
  let isValidRule = true;
  let isValidDistance = true;
  if (conditions && conditions.length > 0) {
    const distanceRules = conditions.filter(
      (c) => c.type === locationAttribute.type
    );
    distanceRules.forEach((distanceRule) => {
      if (distanceRule != null) {
        const distance = distanceRule.value;
        const lat = distanceRule.rangevalues?.[1];
        const lng = distanceRule.rangevalues?.[2];

        const distanceValidation =
          distance === "" ||
          Number.isNaN(Number(distance)) ||
          Number(distance) < 0;
        const latValidation =
          lat === "" ||
          Number.isNaN(Number(lat)) ||
          Number(lat) < -90 ||
          Number(lat) > 90;
        const lngValidation =
          lng === "" ||
          Number.isNaN(Number(lng)) ||
          Number(lng) < -180 ||
          Number(lng) > 180;

        if (distanceValidation || latValidation || lngValidation) {
          isValidDistance = false;
        }
      }
    });
  }
  try {
    const query = workflowConditionsToQuery(conditions, timezone);
    if (query) {
      queryToMongo(query, timezone);
      isValidRule = true;
    }
  } catch {
    isValidRule = false;
  }

  return isValidRule && isValidDistance;
};
export const canBeActivate = (
  conditions: WorkflowCondition[],
  actions: WorkflowActionData[],
  timezone: string
): boolean => {
  return (
    HasConditionsValid(conditions, timezone) &&
    (actions?.length ?? 0) > 0 &&
    !actions.find((a) => a.isInvalid)
  );
};
const WorkflowSwitch: React.FC<{ newWorkflowMode?: boolean }> = ({
  newWorkflowMode = false,
}) => {
  const { t } = useTranslation("common");
  const {
    isValid,
    values: { conditions, actions, active },
    setFieldValue,
    setStatus,
  } = useFormikContext<WorkflowFormData>();
  const { timezone } = useTimeSettings(false);
  // will have to rely on the rule and distanceRule being filled in (even if empty)
  // because using formik/yup validations would also disable the Save button

  const isActive = useMemo(() => {
    return isValid && canBeActivate(conditions, actions, timezone);
  }, [actions, conditions, isValid, timezone]);

  const userActiveValue = useRef<boolean | undefined>(
    newWorkflowMode ? undefined : active
  );

  useEffect(() => {
    if (!isActive) {
      setFieldValue("active", false);
    } else {
      setFieldValue(
        "active",
        userActiveValue.current == null || userActiveValue.current
      );
    }
  }, [isActive, setFieldValue, setStatus]);

  const SwitchLabel = ({ label = "", disabled = false }) => {
    const variant = disabled ? "placeholderText" : "normalText";
    const style = disabled ? undefined : { cursor: "pointer" };

    return (
      <HvTypography
        aria-hidden="true"
        variant={variant}
        style={style}
        onClick={() => {
          userActiveValue.current = !active;
          setFieldValue("active", !active);
        }}
      >
        {label}
      </HvTypography>
    );
  };

  return (
    <>
      <SwitchLabel
        label={t("headerSwitch.labels.inactive")}
        disabled={!isActive}
      />
      <HvSwitch
        id="workflowSwitch"
        aria-label={t("workflowSwitch.ariaLabel")}
        onChange={(event: Event, state: boolean) => {
          userActiveValue.current = !active;
          setFieldValue("active", state);
        }}
        disabled={!isActive}
        checked={active}
      />
      <SwitchLabel
        label={t("headerSwitch.labels.active")}
        disabled={!isActive}
      />
    </>
  );
};

export default WorkflowSwitch;
